import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, CardHeader,
  Button, Chip, Divider, Alert, CircularProgress,
  Switch, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton,
  Collapse, LinearProgress
} from '@mui/material';
import {
  Security, CheckCircle, Cancel, Refresh,
  ExpandMore, ExpandLess, Save, Settings,
  AdminPanelSettings, People, Inventory,
  ShoppingCart, Factory, PointOfSale,
  LocalShipping, AttachMoney, Assessment
} from '@mui/icons-material';
import api from '../services/api';
import { toast } from 'react-hot-toast';

const PermissionManager = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [userPermissions, setUserPermissions] = useState(null);
  const [selectedRoleKey, setSelectedRoleKey] = useState('super_admin');
  const [expandedModules, setExpandedModules] = useState({});
  const [users, setUsers] = useState([]);
  const [permissions, setPermissions] = useState({});

  const ROLES = [
    { key: 'super_admin', label: 'Super Admin', color: '#d32f2f' },
    { key: 'master_admin', label: 'Master Admin', color: '#ed6c02' },
    { key: 'manager', label: 'Manager', color: '#1976d2' },
    { key: 'supervisor', label: 'Supervisor', color: '#0288d1' },
    { key: 'accountant', label: 'Accountant', color: '#2e7d32' },
    { key: 'worker', label: 'Worker', color: '#757575' },
    { key: 'driver', label: 'Driver', color: '#9c27b0' }
  ];

  const MODULE_LABELS = {
    inventory: 'Inventory Management',
    procurement: 'Procurement',
    production: 'Production',
    sales: 'Sales',
    dispatch: 'Dispatch',
    finance: 'Finance',
    hr: 'Human Resources',
    users: 'User Management',
    reports: 'Reports',
    settings: 'Settings'
  };

  const MODULES = ['inventory', 'procurement', 'production', 'sales', 'dispatch', 'finance', 'hr', 'users', 'reports', 'settings'];
  const ACTIONS = ['view', 'create', 'edit', 'delete'];

  const DEFAULT_PERMISSIONS = {
    super_admin: {
      inventory: { view: true, create: true, edit: true, delete: true },
      procurement: { view: true, create: true, edit: true, delete: true },
      production: { view: true, create: true, edit: true, delete: true },
      sales: { view: true, create: true, edit: true, delete: true },
      dispatch: { view: true, create: true, edit: true, delete: true },
      finance: { view: true, create: true, edit: true, delete: true },
      hr: { view: true, create: true, edit: true, delete: true },
      users: { view: true, create: true, edit: true, delete: true },
      reports: { view: true, create: false, edit: false, delete: false },
      settings: { view: true, create: false, edit: true, delete: false }
    },
    master_admin: {
      inventory: { view: true, create: true, edit: true, delete: false },
      procurement: { view: true, create: true, edit: true, delete: false },
      production: { view: true, create: true, edit: true, delete: false },
      sales: { view: true, create: true, edit: true, delete: false },
      dispatch: { view: true, create: true, edit: true, delete: false },
      finance: { view: true, create: true, edit: true, delete: false },
      hr: { view: true, create: true, edit: true, delete: false },
      users: { view: true, create: true, edit: true, delete: false },
      reports: { view: true, create: false, edit: false, delete: false },
      settings: { view: true, create: false, edit: true, delete: false }
    },
    manager: {
      inventory: { view: true, create: true, edit: true, delete: false },
      procurement: { view: true, create: true, edit: true, delete: false },
      production: { view: true, create: true, edit: true, delete: false },
      sales: { view: true, create: true, edit: true, delete: false },
      dispatch: { view: true, create: true, edit: false, delete: false },
      finance: { view: true, create: false, edit: false, delete: false },
      hr: { view: true, create: false, edit: false, delete: false },
      users: { view: false, create: false, edit: false, delete: false },
      reports: { view: true, create: false, edit: false, delete: false },
      settings: { view: false, create: false, edit: false, delete: false }
    },
    supervisor: {
      inventory: { view: true, create: false, edit: false, delete: false },
      procurement: { view: true, create: false, edit: false, delete: false },
      production: { view: true, create: true, edit: false, delete: false },
      sales: { view: true, create: false, edit: false, delete: false },
      dispatch: { view: true, create: false, edit: false, delete: false },
      finance: { view: false, create: false, edit: false, delete: false },
      hr: { view: true, create: false, edit: false, delete: false },
      users: { view: false, create: false, edit: false, delete: false },
      reports: { view: true, create: false, edit: false, delete: false },
      settings: { view: false, create: false, edit: false, delete: false }
    },
    accountant: {
      inventory: { view: false, create: false, edit: false, delete: false },
      procurement: { view: true, create: false, edit: false, delete: false },
      production: { view: false, create: false, edit: false, delete: false },
      sales: { view: true, create: false, edit: false, delete: false },
      dispatch: { view: false, create: false, edit: false, delete: false },
      finance: { view: true, create: true, edit: true, delete: false },
      hr: { view: false, create: false, edit: false, delete: false },
      users: { view: false, create: false, edit: false, delete: false },
      reports: { view: true, create: false, edit: false, delete: false },
      settings: { view: false, create: false, edit: false, delete: false }
    },
    worker: {
      inventory: { view: true, create: false, edit: false, delete: false },
      procurement: { view: false, create: false, edit: false, delete: false },
      production: { view: true, create: true, edit: false, delete: false },
      sales: { view: false, create: false, edit: false, delete: false },
      dispatch: { view: false, create: false, edit: false, delete: false },
      finance: { view: false, create: false, edit: false, delete: false },
      hr: { view: false, create: false, edit: false, delete: false },
      users: { view: false, create: false, edit: false, delete: false },
      reports: { view: false, create: false, edit: false, delete: false },
      settings: { view: false, create: false, edit: false, delete: false }
    },
    driver: {
      inventory: { view: false, create: false, edit: false, delete: false },
      procurement: { view: false, create: false, edit: false, delete: false },
      production: { view: false, create: false, edit: false, delete: false },
      sales: { view: false, create: false, edit: false, delete: false },
      dispatch: { view: true, create: false, edit: true, delete: false },
      finance: { view: false, create: false, edit: false, delete: false },
      hr: { view: false, create: false, edit: false, delete: false },
      users: { view: false, create: false, edit: false, delete: false },
      reports: { view: false, create: false, edit: false, delete: false },
      settings: { view: false, create: false, edit: false, delete: false }
    }
  };

  useEffect(() => {
    setPermissions(DEFAULT_PERMISSIONS);
    fetchUserPermissions();
    fetchUsers();
  }, []);

  const fetchUserPermissions = async () => {
    setLoading(true);
    try {
      const response = await api.get('/auth/permissions/');
      setUserPermissions(response.data);
    } catch (error) {
      console.error('Error fetching permissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/auth/users/');
      // Ensure users is always an array
      const userData = response.data;
      if (Array.isArray(userData)) {
        setUsers(userData);
      } else if (userData && typeof userData === 'object') {
        // If response is an object with results array (DRF pagination)
        if (userData.results && Array.isArray(userData.results)) {
          setUsers(userData.results);
        } else {
          setUsers([]);
        }
      } else {
        setUsers([]);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
    }
  };

  const handleTogglePermission = (role, module, action) => {
    setPermissions(prev => ({
      ...prev,
      [role]: {
        ...prev[role],
        [module]: {
          ...prev[role][module],
          [action]: !prev[role][module][action]
        }
      }
    }));
  };

  const handleToggleModule = (role, module) => {
    const currentModule = permissions[role]?.[module] || { view: false, create: false, edit: false, delete: false };
    const allTrue = Object.values(currentModule).every(v => v === true);
    const newState = !allTrue;
    
    setPermissions(prev => ({
      ...prev,
      [role]: {
        ...prev[role],
        [module]: {
          view: newState,
          create: newState,
          edit: newState,
          delete: newState
        }
      }
    }));
  };

  const handleSavePermissions = async () => {
    setSaving(true);
    try {
      const rolePermissions = permissions[selectedRoleKey] || {};
      
      // Filter users with the selected role
      const usersWithRole = users.filter(u => u.user_type === selectedRoleKey);
      
      if (usersWithRole.length === 0) {
        toast.info(`No users found with role: ${ROLES.find(r => r.key === selectedRoleKey)?.label}`);
        setSaving(false);
        return;
      }
      
      let updatedCount = 0;
      let errorCount = 0;
      
      for (const user of usersWithRole) {
        try {
          const updateData = {
            ...user,
            permissions: rolePermissions
          };
          
          delete updateData.id;
          delete updateData.created_at;
          delete updateData.updated_at;
          delete updateData.created_by;
          delete updateData.created_by_username;
          
          await api.put(`/auth/users/${user.id}/`, updateData);
          updatedCount++;
        } catch (error) {
          console.error(`Error updating user ${user.username}:`, error);
          errorCount++;
        }
      }
      
      toast.success(`Permissions saved! Updated ${updatedCount} user(s), ${errorCount} error(s)`);
      
      await fetchUserPermissions();
      await fetchUsers();
      
    } catch (error) {
      console.error('Error saving permissions:', error);
      toast.error('Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  const handleResetPermissions = () => {
    if (window.confirm('Reset all permissions to default?')) {
      setPermissions(DEFAULT_PERMISSIONS);
      toast.success('Permissions reset to default');
    }
  };

  const toggleModuleExpand = (module) => {
    setExpandedModules(prev => ({
      ...prev,
      [module]: !prev[module]
    }));
  };

  const getRoleLabel = (key) => {
    const role = ROLES.find(r => r.key === key);
    return role ? role.label : key;
  };

  const getModuleIcon = (module) => {
    const icons = {
      inventory: <Inventory />,
      procurement: <ShoppingCart />,
      production: <Factory />,
      sales: <PointOfSale />,
      dispatch: <LocalShipping />,
      finance: <AttachMoney />,
      hr: <People />,
      users: <AdminPanelSettings />,
      reports: <Assessment />,
      settings: <Settings />
    };
    return icons[module] || <Security />;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Ensure users is an array before using map
  const usersList = Array.isArray(users) ? users : [];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          <Security sx={{ mr: 1, verticalAlign: 'middle' }} />
          Permission Manager
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => { fetchUserPermissions(); fetchUsers(); }}
            sx={{ mr: 1 }}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            onClick={handleResetPermissions}
            sx={{ mr: 1, color: '#ed6c02', borderColor: '#ed6c02' }}
          >
            Reset Defaults
          </Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSavePermissions}
            disabled={saving}
          >
            {saving ? <CircularProgress size={24} /> : 'Save Permissions'}
          </Button>
        </Box>
      </Box>

      {userPermissions && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <strong>Your Role:</strong> {getRoleLabel(userPermissions.user_type)}
          {userPermissions.can_manage_users && ' | You can manage user permissions'}
        </Alert>
      )}

      {/* Users by Role Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Users by Role
        </Typography>
        <Grid container spacing={2}>
          {ROLES.map((role) => {
            const usersWithRole = usersList.filter(u => u.user_type === role.key);
            return (
              <Grid item xs={12} sm={6} md={3} key={role.key}>
                <Card 
                  variant="outlined" 
                  sx={{ 
                    borderColor: selectedRoleKey === role.key ? role.color : '#e0e0e0',
                    backgroundColor: selectedRoleKey === role.key ? `${role.color}10` : 'transparent',
                    cursor: 'pointer'
                  }}
                  onClick={() => setSelectedRoleKey(role.key)}
                >
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" fontWeight="bold">
                        {role.label}
                      </Typography>
                      <Chip 
                        label={usersWithRole.length} 
                        size="small"
                        sx={{ backgroundColor: role.color, color: 'white' }}
                      />
                    </Box>
                    <Typography variant="caption" color="textSecondary">
                      {usersWithRole.length > 0 ? usersWithRole.map(u => u.username).join(', ') : 'No users'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Paper>

      {/* Permission Grid */}
      <Grid container spacing={3}>
        {MODULES.map((module) => {
          const modulePerms = permissions[selectedRoleKey]?.[module] || { view: false, create: false, edit: false, delete: false };
          const enabledCount = Object.values(modulePerms).filter(v => v).length;
          const totalActions = Object.keys(modulePerms).length;
          
          return (
            <Grid item xs={12} key={module}>
              <Card>
                <CardHeader
                  title={
                    <Box display="flex" alignItems="center">
                      <Box sx={{ mr: 1 }}>{getModuleIcon(module)}</Box>
                      <Typography variant="h6">
                        {MODULE_LABELS[module]}
                      </Typography>
                      <Chip
                        label={modulePerms.view ? 'Enabled' : 'Disabled'}
                        color={modulePerms.view ? 'success' : 'error'}
                        size="small"
                        sx={{ ml: 2 }}
                      />
                      <Chip
                        label={`${enabledCount}/${totalActions}`}
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    </Box>
                  }
                  action={
                    <Box>
                      <Button
                        size="small"
                        onClick={() => handleToggleModule(selectedRoleKey, module)}
                      >
                        Toggle All
                      </Button>
                      <IconButton onClick={() => toggleModuleExpand(module)}>
                        {expandedModules[module] ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </Box>
                  }
                />
                <Collapse in={expandedModules[module] !== false}>
                  <CardContent>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Action</TableCell>
                            <TableCell align="center">Status</TableCell>
                            <TableCell align="center">Toggle</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {ACTIONS.map((action) => {
                            const isEnabled = modulePerms[action] || false;
                            return (
                              <TableRow key={action}>
                                <TableCell>{action.charAt(0).toUpperCase() + action.slice(1)}</TableCell>
                                <TableCell align="center">
                                  {isEnabled ? (
                                    <CheckCircle color="success" />
                                  ) : (
                                    <Cancel color="error" />
                                  )}
                                </TableCell>
                                <TableCell align="center">
                                  <Switch
                                    checked={isEnabled}
                                    onChange={() => handleTogglePermission(selectedRoleKey, module, action)}
                                    color="primary"
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Collapse>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Summary Section */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Permission Summary for {getRoleLabel(selectedRoleKey)}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          {MODULES.map((module) => {
            const perms = permissions[selectedRoleKey]?.[module] || {};
            const count = Object.values(perms).filter(v => v).length;
            const total = Object.keys(perms).length || 1;
            const percentage = (count / total) * 100;
            return (
              <Grid item xs={12} sm={6} md={4} key={module}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography variant="body2">{MODULE_LABELS[module]}</Typography>
                  <Chip
                    label={`${count}/${total}`}
                    size="small"
                    sx={{
                      backgroundColor: percentage === 100 ? '#4caf50' : percentage > 0 ? '#ff9800' : '#f44336',
                      color: 'white'
                    }}
                  />
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={percentage}
                  sx={{ 
                    mt: 0.5,
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: '#e0e0e0',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: percentage === 100 ? '#4caf50' : percentage > 0 ? '#ff9800' : '#f44336',
                      borderRadius: 4
                    }
                  }}
                />
              </Grid>
            );
          })}
        </Grid>
      </Paper>
    </Box>
  );
};

export default PermissionManager;